import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useProviderLoader } from '@/Providers'
import { InputControl, PasswordControl } from '@/components/form-controls'
import { Button, Divider } from '@/components/nextui'
import { inputValidations } from '@/utils/inputValidations'

interface LoginFormProps {
    onSubmit: (data: any) => Promise<void>
}

export const LoginForm = ({ onSubmit }: LoginFormProps): JSX.Element => {
    const { t } = useTranslation()
    const { control, handleSubmit } = useForm()
    const { showLoader, hideLoader } = useProviderLoader()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleFormSubmit = async (data: any) => {
        try {
            setIsSubmitting(true)
            showLoader()

            // Call the original onSubmit function
            await onSubmit(data)
        } catch (error) {
            console.error('Login error:', error)
        } finally {
            setIsSubmitting(false)
            hideLoader()

            // Clear password from memory
            data.password = ''
        }
    }

    return (
        <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="space-y-4 lg:mx-auto lg:my-0 lg:w-4/5"
        >
            <InputControl
                allowedPattern={inputValidations.emailPattern}
                preserveCase
                name="email"
                control={control}
                label={t('forms.common.email')}
                rules={{
                    required: t('forms.common.required.emailRequired'),
                    pattern: {
                        value: inputValidations.isValidEmail,
                        message: t('createAccount.invalidEmail')
                    }
                }}
                className="text-gray-800"
            />
            <PasswordControl
                showStrength={false}
                name="password"
                control={control}
                label={t('forms.common.password')}
                rules={{
                    required: t('forms.common.required.passwordRequired')
                }}
                className="text-gray-800 dark:text-white"
            />
            <div className="flex justify-evenly items-center">
                <Button
                    type="submit"
                    color="primary"
                    className="w-full"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? t('login.submitting') : t('login.submit')}
                </Button>
            </div>

            <Divider className="flex-1" />

            <Link
                to="/reset-password"
                className="text-xs font-light text-center underline text-disabled block"
            >
                {t('login.forgotPassword')}
            </Link>
            <Link
                to="/resend-confirmation"
                className="text-xs font-light text-center underline text-disabled block"
            >
                {t('login.resendConfirmation')}
            </Link>
            <span className="text-center block">
                {t('login.noAccount')}{' '}
                <Link
                    to="/createAccount"
                    className="cursor-pointer text-primary"
                >
                    {t('createAccount.title')}
                </Link>
            </span>
        </form>
    )
}
